/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the component that provides context for the online patient
 * management system.
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/*
 * Used to get access to router context.
 */
import { useNavigate, useParams } from 'react-router-dom';

/*
 * Used to style components
 */
import { AlertTitle, Box, Button, Grid2, Paper, styled, TextField } from '@mui/material';

/**
 * Used for the basic page layout.
 */
import {
    ALL_INSTITUTIONS_CODE,
    CollapsibleTable,
    InstitutionBreadcrumbs,
    InstitutionsContext,
    PageLayout,
    PatientsByCodesProvider,
    PatientsContext,
    RouteLoading,
    TrialContextSelector,
    useSnackbar
} from '@ngt/opms';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */

/*
 * Used to type patient state.
 */
import RegistrationDialogForm from '@/components/forms/crf/dialog/RegistrationDialogForm';
import { GridColDef, GridEventListener } from '@mui/x-data-grid';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { ChangeEventHandler, Dispatch, FC, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as Dtos from '../api/dtos';
import { useRegisteredPatientColumns } from '../hooks/gridColumns/usePatientColumns';
import { PatientStateType } from '../api/dtos';

/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IRegistrationProps {

}

interface IPatientTableProps {
    title: string;
    entityName: string;
    columns: Array<GridColDef<Dtos.Patient>>;
    onRowClick?: GridEventListener<'rowClick'>;
    patientStates?: Array<number>;
    startCollapsed?: boolean;
    patientsToUse?: Array<Dtos.Patient>;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

//const MemoizedCollapsibleTable = memo(CollapsibleTable);


const SearchBox = styled(Box)(() => ({
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'end'
}));

const SearchTextField = styled(TextField)(({ theme }) => ({
    width: '62%',

    [theme.breakpoints.down('md')]: {
        width: '100%'
    }
}));

const SearchButtonBox = styled(Box)(({ theme }) => ({
    width: '35%',

    [theme.breakpoints.down('md')]: {
        width: '100%'
    }
}));

const PaperContainer = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3)
}));

const AddButton = styled(Button)(({ theme }) => ({
    width: '35%',

    [theme.breakpoints.down('md')]: {
        width: '100%'
    }
}));

const registeredStates: PatientStateType[] = [
    PatientStateType.Baseline,
    PatientStateType.Cycle2,
    PatientStateType.Cycle3,
    PatientStateType.EndOfTreatment,
    PatientStateType.Withdrawn
]

const ineligibleStates: PatientStateType[] = [
    PatientStateType.Ineligible
]


const allStates: PatientStateType[] = [
    PatientStateType.Baseline,
    PatientStateType.Cycle2,
    PatientStateType.Cycle3,
    PatientStateType.EndOfTreatment,
    PatientStateType.Ineligible,
    PatientStateType.Withdrawn
]


const pageSizeOptions = [5, 10, 20, 50, 100];

const PatientTable: FC<IPatientTableProps> = ({
    title,
    entityName,
    columns,
    onRowClick,
    patientStates,
    startCollapsed,
    patientsToUse: patients
}) => {
    const [tableCount, setTableCount] = useState(1);

    const { data: contextPatients, loading: contextPatientsLoading } = useContext(PatientsContext);

    const patientsLoadingToUse = contextPatientsLoading;

    const patientsToUse = useMemo(() => {
        if (patients != null) {
            return patients;
        }

        return contextPatients?.filter(patient => (patientStates ?? []).length === 0 || patientStates?.includes(patient.patientStateId!))

    }, [contextPatients, patientStates, patients])

    useEffect(() => {
        setTableCount(x => x + 1);
    }, [contextPatients, columns, patients]);

    return (
        <>
            <CollapsibleTable
                key={tableCount}
                rows={patientsToUse ?? []}
                loading={patientsLoadingToUse}
                columns={columns as any}
                onRowClick={onRowClick as any}
                //options={tableOptions}
                pageSizeOptions={pageSizeOptions}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                title={title ?? "Participants"}
                pluralizeTitle={false}
                entityName={entityName ?? "Participant"}
                disabledTableText={"There are no participants"}
                startCollapsed={startCollapsed}
            />
        </>

    );
}

const Search: FC = () => {
    const navigate = useNavigate();

    const { data: patients } = useContext(PatientsContext);

    const [searchValue, setSearchValue] = useState<string | null>(null);

    const { enqueueSnackbar } = useSnackbar();

    const saveText: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = useCallback((e) => {
        if (!e.target.value || e.target.value === '') {
            setSearchValue(null);
        }
        else {
            setSearchValue(e.target.value);
        }
    }, [searchValue, setSearchValue]);

    const search = useCallback(() => {
        if (patients?.some(p => p.studyNumber === searchValue)) {
            navigate(`/registration/all-institutions/` + searchValue);
        }
        else {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Participant Not Found
                    </AlertTitle>
                    {searchValue ? <>Participant '{searchValue}' not found. Please ensure you have correctly input the participant's study number.</> : <>The search field is empty.</>}
                </>,
                { variant: 'error' }
            );
        }
    }, [searchValue, navigate]);

    return (
        <SearchBox>
            <SearchTextField
                variant="standard"
                label=" Participant Study Number"
                id='patientNumberSearch'
                onChange={saveText}
            />
            <SearchButtonBox>
                <Button
                    variant='contained'
                    onClick={search}
                    fullWidth
                >
                    Search
                </Button>
            </SearchButtonBox>
        </SearchBox>
    )
}

interface IAddPatientDialogProps {
    registrationFormOpen: boolean,
    setRegistrationFormOpen: Dispatch<SetStateAction<boolean>>,
    institutionCode: string | undefined
}

const AddPatientDialog: FC<IAddPatientDialogProps> = ({
    registrationFormOpen,
    setRegistrationFormOpen,
    institutionCode
}) => {
    const { refresh: refreshPatients } = useContext(PatientsContext);
    const onClose = (_: any, reason: "backdropClick" | "escapeKeyDown") => {
        if (reason != "backdropClick") {
            setRegistrationFormOpen(false);
        }
    }
    return (
        <RegistrationDialogForm
            maxWidth={'md'}
            fullWidth
            open={registrationFormOpen}
            closeForm={() => setRegistrationFormOpen(false)}
            onClose={onClose}
            onAfterSubmit={refreshPatients}
            institutionCode={institutionCode}
            closeOnSubmit
        />
    )
}

const Registration: FC<IRegistrationProps> = () => {
    const navigate = useNavigate();
    
    const [registrationFormOpen, setRegistrationFormOpen] = useState<boolean>(false);
    // const [enrolmentDialogOpen, setEnrolmentDialogOpen] = useState<boolean>(false);
    const { masterGroupCode, collaboratingGroupCode, countryCode, institutionCode } = useParams();

    // const [generalFormContext, setGeneralFormContext] = useState<IGeneralFormContext>({fields: []});

    const parsedInstitutionCode = useMemo(() => {
        return institutionCode !== ALL_INSTITUTIONS_CODE ? institutionCode : undefined;
    }, [institutionCode]);

    const { data: institutions } = useContext(InstitutionsContext);

    const registeredPatientColumns = useRegisteredPatientColumns(institutions ?? null);

    const institutionsToUse = useMemo(() => {
        return institutions?.filter(i => i.code);
    }, [institutions]);

    const institution = useMemo(() => {
        return institutionsToUse?.find(institution => institution.code === parsedInstitutionCode);
    }, [parsedInstitutionCode, institutionsToUse]);

    const {
        permissions: [
            canPatientUpdate
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsPatientUpdate
        ],
        undefined,
        undefined,
        institution?.id
    );

    //@ts-expect-error
    const onSelect = useCallback((newMasterGroupCode?: string | null, newCollaboratingGroupCode?: string | null, newCountryCode?: string | null, newInstitutionCode?: string | null) => {
        if (newInstitutionCode) {
            navigate(`/registration/${newInstitutionCode}`);
        }
        else {
            navigate(`/registration`);
        }

        return;
    }, [navigate]);

    const handleAddParticipantClick = useCallback(() => {
        setRegistrationFormOpen(true);
    }, [setRegistrationFormOpen])

    const onRegisteredRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
        navigate(`/registration/${institutionCode ?? ALL_INSTITUTIONS_CODE}/${params.row?.studyNumber}`);
    }, [navigate, institutionCode, ALL_INSTITUTIONS_CODE]);

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<InstitutionBreadcrumbs />}
            heading="Participants"
        >
            <PaperContainer>
                <PatientsByCodesProvider
                    institutionCode={parsedInstitutionCode}
                    patientStateIds={allStates}
                >
                    <TrialContextSelector
                        onChange={onSelect}
                        allowAllMasterGroups={true}
                        allowAllCollaboratingGroups={true}
                        allowAllCountries={true}
                        allowAllInstitutions={true}
                        hideMasterGroups={true}
                        hideCollaboratingGroups={true}
                        hideCountries={true}
                        masterGroupCode={masterGroupCode}
                        collaboratingGroupCode={collaboratingGroupCode}
                        countryCode={countryCode}
                        institutionCode={institutionCode}
                        institutions={institutionsToUse}
                    >
                        {
                            <Grid2
                                size={{ xs: 12, sm: 6 }}
                            >
                                <Search />
                            </Grid2>
                        }
                        {
                            !!canPatientUpdate &&
                            <Grid2 container size={{ xs: 12 }}>
                                <Grid2 size={{ xs: 12, sm: 6 }}></Grid2>
                                <Grid2
                                    size={{ xs: 12, sm: 6 }}
                                    style={{ display: 'flex', justifyContent: 'end', paddingTop: 0 }}
                                >
                                    <AddButton
                                        variant="contained"
                                        color="primary"
                                        //component={(Link)}
                                        //to={`/registration/${parsedInstitutionCode}/add-participant`}
                                        fullWidth
                                        disabled={!institution}
                                        onClick={handleAddParticipantClick}
                                    >
                                        Add Participant
                                    </AddButton>
                                </Grid2>
                            </Grid2>
                        }
                    </TrialContextSelector>
                    <AddPatientDialog
                        registrationFormOpen={registrationFormOpen}
                        setRegistrationFormOpen={setRegistrationFormOpen}
                        institutionCode={parsedInstitutionCode}
                    />
                    <PatientTable
                        title="Registered"
                        entityName="Participant"
                        onRowClick={onRegisteredRowClick}
                        columns={registeredPatientColumns}
                        patientStates={registeredStates}
                        startCollapsed={false}
                    />
                    <PatientTable
                        title="Ineligible"
                        entityName="Participant"
                        onRowClick={onRegisteredRowClick}
                        columns={registeredPatientColumns}
                        patientStates={ineligibleStates}
                        startCollapsed={false}
                    />
                </PatientsByCodesProvider>
            </PaperContainer>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default Registration;
